import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import useAuth from "../hooks/useAuth";

const DOCLIFT_STYLES = `
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .doclift-page {
      /* Brand */
      --green: #128C7E;          /* WhatsApp teal – used sparingly */
      --green-light: #25D366;    /* WhatsApp bright green – CTA only */
      --green-pale: #E8F5F1;     /* Very light tint for backgrounds */
      --green-mid: #D1EDE7;

      /* Neutrals – warm slate */
      --bg: #FFFFFF;
      --bg-soft: #F7F9F8;
      --bg-alt: #EFF3F2;
      --card: #FFFFFF;
      --border: #E2EAEB;
      --border-soft: #F0F4F4;

      /* Text */
      --text-primary: #0F2920;   /* Near-black with green tint */
      --text-secondary: #4A6A65; /* Mid-tone for body copy */
      --text-muted: #8FA8A4;     /* Labels, captions */

      /* Accents */
      --cta-bg: #25D366;
      --cta-hover: #1ebe5a;

      font-family: 'Inter', sans-serif;
      background: var(--bg);
      color: var(--text-primary);
      overflow-x: hidden;
      line-height: 1.6;
      scroll-behavior: smooth;
    }

    /* ── NAV ── */
    .doclift-page > nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      padding: 0 40px;
      height: 64px;
      display: flex; align-items: center; justify-content: space-between;
      background: rgba(255,255,255,0.92);
      backdrop-filter: blur(16px);
      border-bottom: 1px solid var(--border);
    }
    .doclift-page .nav-logo {
      display: flex; align-items: center; gap: 9px;
      font-size: 1.2rem; font-weight: 800; color: var(--text-primary);
      text-decoration: none; letter-spacing: -0.02em;
    }
    .doclift-page .nav-logo .logo-icon {
      width: 34px; height: 34px; background: var(--green);
      border-radius: 9px; display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 8px rgba(18,140,126,0.25);
    }
    .doclift-page .nav-logo .logo-icon svg { width: 18px; height: 18px; fill: #fff; }
    .doclift-page .nav-cta {
      background: var(--cta-bg); color: #fff;
      padding: 9px 22px; border-radius: 8px;
      font-weight: 600; font-size: 0.875rem;
      text-decoration: none; border: none; cursor: pointer;
      transition: all .2s ease;
      box-shadow: 0 1px 3px rgba(37,211,102,0.3);
    }
    .doclift-page .nav-cta:hover { background: var(--cta-hover); box-shadow: 0 4px 14px rgba(37,211,102,0.35); }

    /* ── HERO ── */
    .doclift-page #hero {
      min-height: 100vh;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      padding: 100px 24px 80px;
      background: linear-gradient(160deg, #F0FAF6 0%, #FFFFFF 55%, #F5FCF8 100%);
      position: relative; overflow: hidden;
    }
    /* Subtle grid dots */
    .doclift-page #hero::before {
      content: '';
      position: absolute; inset: 0;
      background-image: radial-gradient(circle, #C8E6DA 1px, transparent 1px);
      background-size: 32px 32px;
      opacity: 0.45;
      pointer-events: none;
    }
    .doclift-page .hero-badge {
      display: inline-flex; align-items: center; gap: 7px;
      background: var(--green-pale); border: 1px solid var(--green-mid);
      border-radius: 6px; padding: 5px 14px;
      font-size: 0.75rem; font-weight: 700; color: var(--green);
      margin-bottom: 24px; letter-spacing: 0.06em; text-transform: uppercase;
      position: relative;
    }
    .doclift-page .hero-badge .dot { width: 6px; height: 6px; background: var(--green-light); border-radius: 50%; animation: doclift-pulse-dot 1.8s ease-in-out infinite; }
    @keyframes doclift-pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.5)} }

    .doclift-page h1 {
      font-size: clamp(2.2rem, 5.5vw, 4rem);
      font-weight: 900; text-align: center; line-height: 1.12;
      letter-spacing: -0.035em; max-width: 760px;
      color: var(--text-primary);
      margin-bottom: 18px; position: relative;
    }
    .doclift-page h1 span { color: var(--green); }
    .doclift-page .hero-sub {
      font-size: clamp(1rem, 1.8vw, 1.15rem);
      color: var(--text-secondary); text-align: center; max-width: 520px;
      margin-bottom: 36px; font-weight: 400; line-height: 1.7; position: relative;
    }
    .doclift-page .hero-cta-group { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; position: relative; }
    .doclift-page .btn-primary {
      display: inline-flex; align-items: center; gap: 8px;
      background: var(--cta-bg); color: #fff;
      padding: 14px 32px; border-radius: 8px;
      font-weight: 700; font-size: 1rem;
      text-decoration: none; border: none; cursor: pointer;
      transition: all .25s ease;
      box-shadow: 0 2px 8px rgba(37,211,102,0.35);
    }
    .doclift-page .btn-primary:hover { background: var(--cta-hover); transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,211,102,0.4); }
    .doclift-page .btn-primary svg { width: 18px; height: 18px; fill: #fff; }
    .doclift-page .btn-secondary {
      display: inline-flex; align-items: center; gap: 8px;
      background: var(--bg); color: var(--text-primary);
      padding: 14px 28px; border-radius: 8px;
      font-weight: 600; font-size: 1rem;
      text-decoration: none; border: 1.5px solid var(--border);
      transition: all .25s ease;
    }
    .doclift-page .btn-secondary:hover { border-color: var(--green); color: var(--green); }

    /* Trust strip */
    .doclift-page .trust-strip {
      display: flex; align-items: center; gap: 6px;
      margin-top: 28px; color: var(--text-muted); font-size: 0.8rem;
      position: relative;
    }
    .doclift-page .trust-strip .check { color: var(--green-light); font-weight: 700; }

    /* ── FLOW + CHAT ── */
    .doclift-page #flow {
      padding: 96px 24px;
      background: var(--bg-soft);
      border-top: 1px solid var(--border-soft);
      border-bottom: 1px solid var(--border-soft);
    }
    .doclift-page .section-label {
      text-align: center; color: var(--green);
      font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
      letter-spacing: .14em; margin-bottom: 10px;
    }
    .doclift-page .section-title {
      text-align: center; font-size: clamp(1.6rem, 3.5vw, 2.5rem);
      font-weight: 800; margin-bottom: 12px; letter-spacing: -0.025em;
      color: var(--text-primary);
    }
    .doclift-page .section-sub {
      text-align: center; color: var(--text-secondary); font-size: 1rem;
      max-width: 480px; margin: 0 auto 56px; line-height: 1.7;
    }

    /* ── ANIMATED SCENE ── */
    .doclift-page .ai-scene {
      max-width: 960px; margin: 0 auto 56px;
      background: var(--bg-soft);
      border: 1.5px solid var(--border);
      border-radius: 24px; padding: 36px 24px;
      position: relative; overflow: hidden;
    }
    .doclift-page .scene-row {
      display: flex; align-items: center; justify-content: space-between;
      gap: 10px;
    }
    .doclift-page .scene-actor { display: flex; flex-direction: column; align-items: center; gap: 9px; }
    .doclift-page .scene-label { font-size: 0.7rem; font-weight: 600; color: var(--text-muted); text-align: center; }

    /* ---- PHONE ---- */
    .doclift-page .phone-mock {
      width: 172px; flex-shrink: 0;
      background: #1C1C1E; border-radius: 22px;
      border: 2.5px solid #3A3A3C;
      overflow: hidden;
      box-shadow: 0 14px 36px rgba(0,0,0,0.24);
    }
    .doclift-page .phone-bar {
      background: #075E54; padding: 10px 12px;
      display: flex; align-items: center; gap: 8px;
    }
    .doclift-page .phone-bar-avatar {
      width: 30px; height: 30px; border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem; flex-shrink: 0;
    }
    .doclift-page .phone-bar-info .pname { font-size: 0.75rem; font-weight: 700; color: #fff; }
    .doclift-page .phone-bar-info .pstatus { font-size: 0.6rem; color: rgba(255,255,255,0.7); }
    .doclift-page .phone-chat-body {
      background: #ECE5DD; padding: 10px 8px;
      display: flex; flex-direction: column; gap: 8px;
      min-height: 160px;
    }
    .doclift-page .pb {
      border-radius: 10px; padding: 7px 11px;
      font-size: 0.75rem; color: #222; line-height: 1.5;
      max-width: 90%; box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    .doclift-page .pb.q { background:#fff; border-radius:10px 10px 10px 2px; align-self:flex-start;
      opacity:0; animation: doclift-q-appear 14s infinite; }
    .doclift-page .pb.a { background:#DCF8C6; border-radius:10px 10px 2px 10px; align-self:flex-end;
      opacity:0; animation: doclift-a-appear 14s infinite; }
    .doclift-page .p-typing {
      display:flex; gap:3px; align-items:center; padding:7px 11px;
      background:#fff; border-radius:10px; width:fit-content;
      opacity:0; animation: doclift-typing-show 14s infinite;
      box-shadow:0 1px 3px rgba(0,0,0,0.08);
    }
    .doclift-page .p-typing span {
      width:5px; height:5px; border-radius:50%; background:#8FA99A;
      animation: doclift-tdot 0.7s infinite;
    }
    .doclift-page .p-typing span:nth-child(2){animation-delay:.15s}
    .doclift-page .p-typing span:nth-child(3){animation-delay:.3s}
    @keyframes doclift-tdot{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
    @keyframes doclift-q-appear{
      0%,8%   {opacity:0;transform:scale(.92) translateY(5px)}
      12%,92% {opacity:1;transform:scale(1) translateY(0)}
      96%,100%{opacity:0}
    }
    @keyframes doclift-typing-show{
      0%,70%  {opacity:0}
      73%,82% {opacity:1}
      86%,100%{opacity:0}
    }
    @keyframes doclift-a-appear{
      0%,83%  {opacity:0;transform:translateY(6px)}
      87%,94% {opacity:1;transform:translateY(0)}
      97%,100%{opacity:0}
    }

    /* ---- PIPES ---- */
    .doclift-page .scene-pipe {
      flex:1; height:32px; position:relative; min-width:36px;
    }
    .doclift-page .pipe-track {
      position:absolute; left:0; right:0; top:50%;
      height:2px; background:var(--border); transform:translateY(-50%);
    }
    .doclift-page .pipe-dot {
      position:absolute; top:50%; transform:translateY(-50%);
      width:10px; height:10px; border-radius:50%;
      background:var(--green); opacity:0; z-index:1;
    }
    /* Left pipe - question LTR (phone→AI) */
    .doclift-page .pipe-l>div:nth-child(2){animation:doclift-dot-ltr-q 14s infinite}
    .doclift-page .pipe-l>div:nth-child(3){animation:doclift-dot-ltr-q 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-l>div:nth-child(4){animation:doclift-dot-ltr-q 14s infinite;animation-delay:.5s}
    /* Left pipe - reply RTL (AI→phone) */
    .doclift-page .pipe-l>div:nth-child(5){animation:doclift-dot-rtl-rpl 14s infinite}
    .doclift-page .pipe-l>div:nth-child(6){animation:doclift-dot-rtl-rpl 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-l>div:nth-child(7){animation:doclift-dot-rtl-rpl 14s infinite;animation-delay:.5s}
    /* Right pipe - query LTR (AI→KB) */
    .doclift-page .pipe-r>div:nth-child(2){animation:doclift-dot-ltr-kb 14s infinite}
    .doclift-page .pipe-r>div:nth-child(3){animation:doclift-dot-ltr-kb 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-r>div:nth-child(4){animation:doclift-dot-ltr-kb 14s infinite;animation-delay:.5s}
    /* Right pipe - answer RTL (KB→AI) */
    .doclift-page .pipe-r>div:nth-child(5){animation:doclift-dot-rtl-kb 14s infinite}
    .doclift-page .pipe-r>div:nth-child(6){animation:doclift-dot-rtl-kb 14s infinite;animation-delay:.25s}
    .doclift-page .pipe-r>div:nth-child(7){animation:doclift-dot-rtl-kb 14s infinite;animation-delay:.5s}
    @keyframes doclift-dot-ltr-q{
      0%,10%  {left:0%;  opacity:0}
      12%     {left:0%;  opacity:1}
      26%     {left:100%;opacity:1}
      28%,100%{left:100%;opacity:0}
    }
    @keyframes doclift-dot-ltr-kb{
      0%,27%  {left:0%;  opacity:0}
      29%     {left:0%;  opacity:1}
      42%     {left:100%;opacity:1}
      44%,100%{left:100%;opacity:0}
    }
    @keyframes doclift-dot-rtl-kb{
      0%,43%  {left:100%;opacity:0}
      45%     {left:100%;opacity:1}
      59%     {left:0%;  opacity:1}
      61%,100%{left:0%;  opacity:0}
    }
    @keyframes doclift-dot-rtl-rpl{
      0%,60%  {left:100%;opacity:0}
      62%     {left:100%;opacity:1}
      75%     {left:0%;  opacity:1}
      77%,100%{left:0%;  opacity:0}
    }

    /* ---- AI CENTER ---- */
    .doclift-page .ai-center {
      flex-shrink:0; width:128px;
      background:var(--bg); border:1.5px solid var(--green-mid);
      border-radius:18px; padding:14px 10px; text-align:center;
      animation:doclift-ai-glow 14s infinite;
    }
    @keyframes doclift-ai-glow{
      0%,26%  {box-shadow:none;border-color:var(--green-mid)}
      29%,62% {box-shadow:0 0 0 5px rgba(18,140,126,0.1),0 0 28px rgba(18,140,126,0.2);border-color:var(--green)}
      65%,100%{box-shadow:none;border-color:var(--green-mid)}
    }
    .doclift-page .ai-center-icon{font-size:2.2rem;line-height:1;margin-bottom:5px}
    .doclift-page .ai-center-name{font-size:0.7rem;font-weight:800;color:var(--green);margin-bottom:8px}
    .doclift-page .ai-thinking{
      font-size:0.56rem;color:var(--text-muted);
      background:var(--green-pale);border-radius:5px;padding:3px 7px;
      opacity:0;animation:doclift-thinking-show 14s infinite;
    }
    @keyframes doclift-thinking-show{
      0%,26%  {opacity:0}
      29%,62% {opacity:1}
      65%,100%{opacity:0}
    }

    /* ---- KNOWLEDGE BASE PANEL ---- */
    .doclift-page .kb-panel {
      flex-shrink:0; width:180px;
      background:var(--bg); border:1.5px solid var(--border);
      border-radius:18px; padding:14px 12px;
      animation:doclift-kb-panel-glow 14s infinite;
    }
    @keyframes doclift-kb-panel-glow{
      0%,41%  {box-shadow:none;border-color:var(--border)}
      44%,61% {box-shadow:0 0 0 4px rgba(18,140,126,0.08),0 0 20px rgba(18,140,126,0.14);border-color:var(--green-mid)}
      64%,100%{box-shadow:none;border-color:var(--border)}
    }
    .doclift-page .kb-panel-head{
      display:flex;align-items:center;gap:6px;
      font-size:0.68rem;font-weight:800;color:var(--text-primary);margin-bottom:3px;
    }
    .doclift-page .kb-panel-sub{font-size:0.58rem;color:var(--text-muted);margin-bottom:10px}
    .doclift-page .kb-rows{display:flex;flex-direction:column;gap:5px}
    .doclift-page .kb-row{
      background:var(--bg-soft);border:1px solid var(--border);
      border-radius:7px;padding:5px 8px;
      font-size:0.63rem;color:var(--text-secondary);
      display:flex;align-items:center;gap:5px;
      position:relative;overflow:hidden;text-align:left;
    }
    .doclift-page .kb-row::after{
      content:'';position:absolute;
      top:0;left:-100%;bottom:0;width:65%;
      background:linear-gradient(90deg,transparent,rgba(37,211,102,0.28),transparent);
    }
    .doclift-page .kb-row:nth-child(1)::after{animation:doclift-kb-sweep 14s infinite 0s}
    .doclift-page .kb-row:nth-child(2)::after{animation:doclift-kb-sweep 14s infinite .5s}
    .doclift-page .kb-row:nth-child(3)::after{animation:doclift-kb-sweep 14s infinite 1s}
    .doclift-page .kb-row:nth-child(4)::after{animation:doclift-kb-sweep 14s infinite 1.5s}
    @keyframes doclift-kb-sweep{
      0%,41%  {left:-100%;opacity:0}
      43%     {left:-100%;opacity:1}
      58%     {left:150%;opacity:1}
      60%,100%{left:150%;opacity:0}
    }
    .doclift-page .kb-row.kb-match{animation:doclift-kb-match-anim 14s infinite}
    @keyframes doclift-kb-match-anim{
      0%,42%  {background:var(--bg-soft);border-color:var(--border);color:var(--text-secondary)}
      47%,58% {background:var(--green-pale);border-color:var(--green-mid);color:var(--green)}
      63%,100%{background:var(--bg-soft);border-color:var(--border);color:var(--text-secondary)}
    }

    /* scene mobile */
    @media (max-width: 720px) {
      .doclift-page .ai-scene { display: none; }
    }

    /* CHAT DEMO */
    .doclift-page .chat-demo-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
      max-width: 800px; margin: 0 auto;
    }
    .doclift-page .chat-window {
      background: var(--card);
      border: 1px solid var(--border);
      border-radius: 16px; overflow: hidden;
      box-shadow: 0 4px 24px rgba(0,0,0,0.07);
    }
    .doclift-page .chat-header {
      background: #075E54; padding: 12px 16px;
      display: flex; align-items: center; gap: 10px;
    }
    .doclift-page .chat-avatar {
      width: 34px; height: 34px; border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem;
    }
    .doclift-page .chat-header-info .name { font-weight: 700; font-size: 0.88rem; color: #fff; }
    .doclift-page .chat-header-info .status { font-size: 0.7rem; color: rgba(255,255,255,0.7); }
    .doclift-page .chat-body { padding: 14px; display: flex; flex-direction: column; gap: 10px; background: #ECE5DD; }
    .doclift-page .bubble {
      padding: 9px 12px; border-radius: 12px;
      font-size: 0.82rem; max-width: 85%; line-height: 1.5;
    }
    .doclift-page .bubble.customer { background: #fff; align-self: flex-start; border-bottom-left-radius: 3px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); color: #303030; }
    .doclift-page .bubble.ai { background: #DCF8C6; align-self: flex-end; border-bottom-right-radius: 3px; box-shadow: 0 1px 3px rgba(0,0,0,0.08); color: #303030; }
    .doclift-page .bubble .sender { font-size: 0.66rem; font-weight: 700; margin-bottom: 2px; }
    .doclift-page .bubble.customer .sender { color: #8FA99A; }
    .doclift-page .bubble.ai .sender { color: #128C7E; }

    /* ── BENEFITS ── */
    .doclift-page #benefits {
      padding: 96px 24px;
      background: var(--bg);
    }
    .doclift-page .benefits-grid {
      display: grid; grid-template-columns: repeat(3,1fr); gap: 20px;
      max-width: 940px; margin: 0 auto;
    }
    .doclift-page .benefit-card {
      background: var(--bg-soft);
      border: 1.5px solid var(--border);
      border-radius: 16px; padding: 32px 26px;
      transition: all .25s ease;
    }
    .doclift-page .benefit-card:hover { transform: translateY(-4px); border-color: var(--green-mid); box-shadow: 0 12px 36px rgba(18,140,126,0.1); }
    .doclift-page .benefit-icon-wrap {
      width: 52px; height: 52px; border-radius: 14px;
      background: var(--green-pale); border: 1.5px solid var(--green-mid);
      display: flex; align-items: center; justify-content: center;
      font-size: 1.5rem; margin-bottom: 18px;
    }
    .doclift-page .benefit-title { font-size: 1.05rem; font-weight: 700; margin-bottom: 8px; color: var(--text-primary); }
    .doclift-page .benefit-desc { font-size: 0.875rem; color: var(--text-secondary); line-height: 1.65; }

    /* ── HOW IT WORKS ── */
    .doclift-page #howitworks {
      padding: 96px 24px;
      background: var(--bg-soft);
      border-top: 1px solid var(--border-soft);
    }
    .doclift-page .steps-row {
      display: flex; gap: 0; max-width: 820px; margin: 0 auto;
      position: relative;
    }
    .doclift-page .steps-row::before {
      content: '';
      position: absolute; top: 28px; left: 18%; right: 18%; height: 2px;
      background: linear-gradient(90deg, var(--green-mid), var(--border));
    }
    .doclift-page .step {
      flex: 1; display: flex; flex-direction: column; align-items: center;
      text-align: center; padding: 0 20px; position: relative;
    }
    .doclift-page .step-num {
      width: 56px; height: 56px; border-radius: 50%;
      background: var(--bg); border: 2px solid var(--green);
      display: flex; align-items: center; justify-content: center;
      font-size: 1.5rem; margin-bottom: 20px; position: relative; z-index: 1;
      box-shadow: 0 0 0 6px var(--green-pale);
    }
    .doclift-page .step-title { font-size: 0.95rem; font-weight: 700; margin-bottom: 6px; color: var(--text-primary); }
    .doclift-page .step-desc { font-size: 0.83rem; color: var(--text-secondary); line-height: 1.65; }

    /* ── WHO IS IT FOR ── */
    .doclift-page #for-whom {
      padding: 96px 24px;
      background: var(--bg);
      text-align: center;
    }
    .doclift-page .for-whom-visual {
      display: flex; flex-wrap: wrap; justify-content: center; gap: 12px;
      max-width: 680px; margin: 0 auto;
    }
    .doclift-page .segment-pill {
      display: flex; align-items: center; gap: 7px;
      background: var(--bg-soft); border: 1.5px solid var(--border);
      border-radius: 8px; padding: 9px 18px;
      font-size: 0.85rem; font-weight: 500; color: var(--text-secondary);
      transition: all .2s ease; cursor: default;
    }
    .doclift-page .segment-pill:hover { border-color: var(--green); color: var(--green); background: var(--green-pale); }
    .doclift-page .segment-pill .seg-icon { font-size: 1rem; }

    /* ── AI POSITIONING ── */
    .doclift-page #ai-section {
      padding: 80px 24px;
      background: var(--bg-soft);
      border-top: 1px solid var(--border-soft);
    }
    .doclift-page .ai-card {
      max-width: 680px; margin: 0 auto;
      background: var(--bg);
      border: 1.5px solid var(--green-mid);
      border-radius: 20px; padding: 52px 44px; text-align: center;
      box-shadow: 0 8px 40px rgba(18,140,126,0.08);
    }
    .doclift-page .ai-card .ai-icon { font-size: 2.4rem; margin-bottom: 16px; }
    .doclift-page .ai-card h2 { font-size: clamp(1.4rem, 3vw, 2rem); font-weight: 800; margin-bottom: 14px; letter-spacing: -0.02em; color: var(--text-primary); }
    .doclift-page .ai-card p { color: var(--text-secondary); font-size: 0.975rem; line-height: 1.75; }

    /* ── FINAL CTA ── */
    .doclift-page #final-cta {
      padding: 100px 24px;
      background: linear-gradient(160deg, #0B4A3C 0%, #0D5C48 50%, #075E54 100%);
      text-align: center; position: relative; overflow: hidden;
    }
    .doclift-page #final-cta::before {
      content: '';
      position: absolute; inset: 0;
      background-image: radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px);
      background-size: 28px 28px;
      pointer-events: none;
    }
    .doclift-page #final-cta h2 {
      font-size: clamp(1.8rem, 4vw, 2.8rem);
      font-weight: 800; letter-spacing: -0.025em;
      max-width: 580px; margin: 0 auto 14px;
      color: #fff; position: relative;
    }
    .doclift-page #final-cta p { color: rgba(255,255,255,0.7); margin-bottom: 36px; font-size: 1rem; position: relative; }
    .doclift-page #final-cta .btn-cta-white {
      display: inline-flex; align-items: center; gap: 9px;
      background: #fff; color: var(--green);
      padding: 14px 32px; border-radius: 8px;
      font-weight: 700; font-size: 1rem;
      text-decoration: none;
      transition: all .25s ease;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      position: relative;
    }
    .doclift-page #final-cta .btn-cta-white:hover { transform: translateY(-2px); box-shadow: 0 10px 30px rgba(0,0,0,0.2); }
    .doclift-page #final-cta .btn-cta-white svg { width: 18px; height: 18px; fill: var(--green); }

    /* ── FOOTER ── */
    .doclift-page footer {
      padding: 28px 40px;
      background: #063830;
      border-top: 1px solid rgba(255,255,255,0.06);
      display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
    }
    .doclift-page footer .brand { font-weight: 700; color: rgba(255,255,255,0.5); font-size: 0.875rem; }
    .doclift-page footer .tagline { font-size: 0.78rem; color: rgba(255,255,255,0.3); }

    /* ── ANIMATIONS ── */
    .doclift-page .fade-up {
      opacity: 0; transform: translateY(28px);
      transition: opacity .55s ease, transform .55s ease;
    }
    .doclift-page .fade-up.visible { opacity: 1; transform: translateY(0); }
    .doclift-page .delay-1 { transition-delay: .08s; }
    .doclift-page .delay-2 { transition-delay: .16s; }
    .doclift-page .delay-3 { transition-delay: .24s; }
    .doclift-page .delay-4 { transition-delay: .32s; }

    /* ── PRICING ── */
    .doclift-page #pricing {
      padding: 96px 24px;
      background: var(--bg);
    }
    .doclift-page .pricing-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 20px;
      max-width: 760px; margin: 0 auto;
      align-items: start;
    }
    .doclift-page .price-card {
      border-radius: 20px; padding: 36px 32px;
      border: 1.5px solid var(--border);
      background: var(--bg-soft);
      transition: transform .25s ease;
    }
    .doclift-page .price-card:hover { transform: translateY(-3px); }
    .doclift-page .price-card.featured {
      background: linear-gradient(145deg, #0B5C48, #0D6B53);
      border-color: transparent;
      box-shadow: 0 12px 48px rgba(11,92,72,0.28);
      color: #fff;
    }
    .doclift-page .price-badge {
      display: inline-block;
      background: #25D366; color: #fff;
      font-size: 0.7rem; font-weight: 800;
      text-transform: uppercase; letter-spacing: .1em;
      padding: 4px 12px; border-radius: 6px;
      margin-bottom: 20px;
    }
    .doclift-page .price-card.featured .price-badge { background: rgba(255,255,255,0.2); }
    .doclift-page .price-plan-name {
      font-size: 1rem; font-weight: 700; margin-bottom: 8px;
    }
    .doclift-page .price-card.featured .price-plan-name { color: rgba(255,255,255,0.75); }
    .doclift-page .price-card:not(.featured) .price-plan-name { color: var(--text-secondary); }
    .doclift-page .price-amount {
      font-size: 3.6rem; font-weight: 900; line-height: 1; letter-spacing: -0.04em;
      margin-bottom: 6px;
    }
    .doclift-page .price-card.featured .price-amount { color: #fff; }
    .doclift-page .price-card:not(.featured) .price-amount { color: var(--text-primary); }
    .doclift-page .price-amount .currency { font-size: 1.6rem; font-weight: 700; vertical-align: super; margin-right: 2px; }
    .doclift-page .price-amount .period { font-size: 1rem; font-weight: 500; opacity: 0.6; }
    .doclift-page .price-note {
      font-size: 0.82rem; margin-bottom: 26px; line-height: 1.5;
    }
    .doclift-page .price-card.featured .price-note { color: rgba(255,255,255,0.65); }
    .doclift-page .price-card:not(.featured) .price-note { color: var(--text-muted); }
    .doclift-page .price-features {
      list-style: none; display: flex; flex-direction: column; gap: 11px;
    }
    .doclift-page .price-features li {
      display: flex; align-items: flex-start; gap: 10px;
      font-size: 0.875rem; line-height: 1.5;
    }
    .doclift-page .price-card.featured .price-features li { color: rgba(255,255,255,0.85); }
    .doclift-page .price-card:not(.featured) .price-features li { color: var(--text-secondary); }
    .doclift-page .price-features .tick {
      flex-shrink: 0; width: 20px; height: 20px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 0.7rem; font-weight: 800; margin-top: 1px;
    }
    .doclift-page .price-card.featured .tick { background: rgba(255,255,255,0.2); color: #fff; }
    .doclift-page .price-card:not(.featured) .tick { background: var(--green-pale); color: var(--green); }
    .doclift-page .price-divider {
      display: flex; align-items: center; gap: 16px;
      max-width: 760px; margin: 0 auto 0; padding-top: 24px;
      color: var(--text-muted); font-size: 0.8rem;
    }
    .doclift-page .price-divider::before, .doclift-page .price-divider::after {
      content: ''; flex: 1; height: 1px; background: var(--border);
    }
    .doclift-page .zero-fees-row {
      display: flex; flex-wrap: wrap; justify-content: center; gap: 20px;
      max-width: 620px; margin: 28px auto 0;
    }
    .doclift-page .zero-fee-item {
      display: flex; align-items: center; gap: 7px;
      font-size: 0.82rem; color: var(--text-secondary);
    }
    .doclift-page .zero-fee-item .zf-icon {
      width: 28px; height: 28px; border-radius: 8px;
      background: var(--green-pale); border: 1px solid var(--green-mid);
      display: flex; align-items: center; justify-content: center;
      font-size: 0.85rem; flex-shrink: 0;
    }

    /* ── RESPONSIVE ── */
    @media (max-width: 768px) {
      .doclift-page .pricing-grid { grid-template-columns: 1fr; }
      .doclift-page > nav { padding: 0 20px; }
      .doclift-page .benefits-grid { grid-template-columns: 1fr; }
      .doclift-page .chat-demo-grid { grid-template-columns: 1fr; }
      .doclift-page .steps-row { flex-direction: column; gap: 36px; }
      .doclift-page .steps-row::before { display: none; }
      .doclift-page .ai-card { padding: 36px 24px; }
      .doclift-page footer { flex-direction: column; text-align: center; }
      .doclift-page .flow-arrow { display: none; }
    }
    @media (max-width: 480px) {
      .doclift-page .hero-cta-group { flex-direction: column; align-items: center; }
      .doclift-page .price-amount { font-size: 2.8rem; }
    }
`;

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12.004 2.003C6.486 2.003 2 6.49 2 12.008c0 1.757.456 3.468 1.325 4.978L2 22l5.156-1.303A9.954 9.954 0 0012.004 22c5.518 0 10.004-4.487 10.004-10.005C22.004 6.483 17.52 2 12.004 2zm-.002 18.207a8.285 8.285 0 01-4.217-1.152l-.303-.179-3.062.774.808-2.979-.197-.307A8.205 8.205 0 013.797 12c0-4.553 3.654-8.207 8.207-8.207 4.551 0 8.205 3.654 8.203 8.207 0 4.551-3.654 8.207-8.205 8.207z" />
  </svg>
);

const HomeComponent = () => {
  const navRef = useRef(null);
  const rootRef = useRef(null);
  const { user: userInfo } = useAuth();

  useEffect(() => {
    document.title = "DocLift – Turn WhatsApp into Your Business Assistant";

    const root = rootRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("visible");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    root?.querySelectorAll(".fade-up").forEach((el) => {
      if (!el.classList.contains("visible")) observer.observe(el);
    });

    const handleScroll = () => {
      if (navRef.current) {
        navRef.current.style.boxShadow =
          window.scrollY > 40 ? "0 2px 16px rgba(0,0,0,0.07)" : "none";
      }
    };
    window.addEventListener("scroll", handleScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="doclift-page" ref={rootRef}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />
      <style>{DOCLIFT_STYLES}</style>

      {/* NAV */}
      {userInfo ? (
        <Header />
      ) : (
        <nav ref={navRef}>
          <a className="nav-logo" href="#hero" id="nav-logo">
            <div className="logo-icon">
              <WhatsAppIcon />
            </div>
            DocLift
          </a>
          <Link to="/dashboard" className="nav-cta" id="nav-get-started">
            Get Started
          </Link>
        </nav>
      )}

      {/* HERO */}
      <section id="hero">
        <div className="hero-badge fade-up visible">
          <span className="dot"></span> WhatsApp Business AI
        </div>
        <h1 className="fade-up visible delay-1">
          Turn WhatsApp into your <span>business assistant.</span>
        </h1>
        <p className="hero-sub fade-up visible delay-2">
          Connect your WhatsApp. Chat with customers. Let AI handle the routine questions.
        </p>
        <div className="hero-cta-group fade-up visible delay-3">
          <Link to="/dashboard" className="btn-primary" id="hero-get-started">
            <WhatsAppIcon />
            Get Started — It's Free
          </Link>
          <a href="#flow" className="btn-secondary" id="hero-see-how">
            See how it works
          </a>
        </div>
        <div className="trust-strip fade-up visible delay-4">
          <span className="check">&#10003;</span> No credit card needed &nbsp;&nbsp;
          <span className="check">&#10003;</span> Setup in 5 minutes &nbsp;&nbsp;
          <span className="check">&#10003;</span> Works on your existing number
        </div>
      </section>

      {/* FLOW + CHAT DEMO */}
      <section id="flow">
        <p className="section-label fade-up">At a glance</p>
        <h2 className="section-title fade-up delay-1">See it work in real conversations</h2>
        <p className="section-sub fade-up delay-2">
          Your customers WhatsApp you. DocLift replies — instantly, using your own business
          information.
        </p>

        <div className="ai-scene fade-up delay-2">
          <div className="scene-row">
            {/* CUSTOMER PHONE */}
            <div className="scene-actor">
              <div className="phone-mock">
                <div className="phone-bar">
                  <div className="phone-bar-avatar">&#128100;</div>
                  <div className="phone-bar-info">
                    <div className="pname">Priya S.</div>
                    <div className="pstatus">&#9679; Online</div>
                  </div>
                </div>
                <div className="phone-chat-body">
                  <div className="pb q">What are your timings? &#128336;</div>
                  <div className="p-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="pb a">We're open 10 AM &ndash; 8 PM, Mon&ndash;Sat &#9989;</div>
                </div>
              </div>
              <div className="scene-label">Customer (WhatsApp)</div>
            </div>

            {/* LEFT PIPE */}
            <div className="scene-pipe pipe-l">
              <div className="pipe-track"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
            </div>

            {/* AI BOT CENTER */}
            <div className="scene-actor">
              <div className="ai-center">
                <div className="ai-center-icon">&#129302;</div>
                <div className="ai-center-name">DocLift AI</div>
                <div className="ai-thinking">&#128269; Searching&hellip;</div>
              </div>
              <div className="scene-label">AI processes</div>
            </div>

            {/* RIGHT PIPE */}
            <div className="scene-pipe pipe-r">
              <div className="pipe-track"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
              <div className="pipe-dot"></div>
            </div>

            {/* KNOWLEDGE BASE PANEL */}
            <div className="scene-actor">
              <div className="kb-panel">
                <div className="kb-panel-head">&#128193; Knowledge Base</div>
                <div className="kb-panel-sub">Your business information</div>
                <div className="kb-rows">
                  <div className="kb-row kb-match">&#128197; Timings &amp; Hours</div>
                  <div className="kb-row">&#128176; Pricing &amp; Services</div>
                  <div className="kb-row">&#128666; Delivery Areas</div>
                  <div className="kb-row">&#128222; Contact &amp; FAQs</div>
                </div>
              </div>
              <div className="scene-label">Business Knowledge Base</div>
            </div>
          </div>
        </div>

        <div className="chat-demo-grid fade-up delay-3">
          <div className="chat-window">
            <div className="chat-header">
              <div className="chat-avatar">&#129489;</div>
              <div className="chat-header-info">
                <div className="name">Priya Sharma</div>
                <div className="status">&#9679; Online</div>
              </div>
            </div>
            <div className="chat-body">
              <div className="bubble customer">
                <div className="sender">Customer</div>
                What are your timings?
              </div>
              <div className="bubble ai">
                <div className="sender">&#129302; DocLift AI</div>
                We're open 10 AM – 8 PM (Mon–Sat). Sundays 11 AM – 6 PM. Anything else I can help
                with?
              </div>
              <div className="bubble customer">
                <div className="sender">Customer</div>
                Do you deliver in Gurgaon?
              </div>
              <div className="bubble ai">
                <div className="sender">&#129302; DocLift AI</div>
                Yes! We deliver across all of Gurgaon. Free delivery on orders above &#8377;500.{" "}
                &#128757;
              </div>
            </div>
          </div>

          <div className="chat-window">
            <div className="chat-header">
              <div className="chat-avatar">&#128104;</div>
              <div className="chat-header-info">
                <div className="name">Rahul Verma</div>
                <div className="status">&#9679; Online</div>
              </div>
            </div>
            <div className="chat-body">
              <div className="bubble customer">
                <div className="sender">Customer</div>
                Can I book an appointment for Saturday?
              </div>
              <div className="bubble ai">
                <div className="sender">&#129302; DocLift AI</div>
                Absolutely! Slots available at 11 AM, 2 PM and 5 PM on Saturday. Which works for
                you?
              </div>
              <div className="bubble customer">
                <div className="sender">Customer</div>
                What's the price for a haircut?
              </div>
              <div className="bubble ai">
                <div className="sender">&#129302; DocLift AI</div>
                Haircuts start at &#8377;299. We also have combo packages. Want me to share the
                full menu? &#128136;
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits">
        <p className="section-label fade-up">Why DocLift</p>
        <h2 className="section-title fade-up delay-1">Everything you need. Nothing extra.</h2>
        <p className="section-sub fade-up delay-2">
          Simple tools built around how small businesses actually work.
        </p>
        <div className="benefits-grid">
          <div className="benefit-card fade-up delay-1">
            <div className="benefit-icon-wrap">&#128172;</div>
            <div className="benefit-title">Talk to customers</div>
            <div className="benefit-desc">
              Manage all your customer conversations from one simple place.
            </div>
          </div>
          <div className="benefit-card fade-up delay-2">
            <div className="benefit-icon-wrap">&#129302;</div>
            <div className="benefit-title">Let AI answer</div>
            <div className="benefit-desc">
              Automatically reply to common questions — timings, pricing, delivery, availability.
            </div>
          </div>
          <div className="benefit-card fade-up delay-3">
            <div className="benefit-icon-wrap">&#128200;</div>
            <div className="benefit-title">Never miss a customer</div>
            <div className="benefit-desc">
              Respond instantly, even when you're busy, closed, or unavailable.
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="howitworks">
        <p className="section-label fade-up">Setup</p>
        <h2 className="section-title fade-up delay-1">Up and running in minutes</h2>
        <p className="section-sub fade-up delay-2">Three steps — no technical knowledge required.</p>
        <div className="steps-row">
          <div className="step fade-up delay-1">
            <div className="step-num">&#128241;</div>
            <div className="step-title">Connect WhatsApp</div>
            <div className="step-desc">Link your existing business WhatsApp number in one click.</div>
          </div>
          <div className="step fade-up delay-2">
            <div className="step-num">&#128221;</div>
            <div className="step-title">Add your info</div>
            <div className="step-desc">
              Products, services, FAQs, pricing — just paste in plain language.
            </div>
          </div>
          <div className="step fade-up delay-3">
            <div className="step-num">&#9989;</div>
            <div className="step-title">Start chatting</div>
            <div className="step-desc">
              DocLift answers customers automatically using your information.
            </div>
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section id="for-whom">
        <p className="section-label fade-up">Who it's for</p>
        <h2 className="section-title fade-up delay-1">Made for small businesses</h2>
        <p className="section-sub fade-up delay-2">
          If you talk to customers on WhatsApp, DocLift is for you.
        </p>
        <div className="for-whom-visual fade-up delay-2">
          <div className="segment-pill">
            <span className="seg-icon">&#128717;</span> Retailers
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#127973;</span> Clinics
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#128136;</span> Salons
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#127869;</span> Restaurants
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#128295;</span> Service Businesses
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#128084;</span> Professionals
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#128666;</span> Local Delivery
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#129740;</span> Beauty &amp; Wellness
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#128218;</span> Tutors &amp; Coaches
          </div>
          <div className="segment-pill">
            <span className="seg-icon">&#127959;</span> Contractors
          </div>
        </div>
      </section>

      {/* AI POSITIONING */}
      <section id="ai-section">
        <div className="ai-card fade-up">
          <div className="ai-icon">&#127970;</div>
          <h2>Your business. Your knowledge. Your AI.</h2>
          <p>
            DocLift answers customers using only the information <strong>you</strong> provide.
            <br />
            No guesswork. No surprises. Just your business, available 24/7.
          </p>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing">
        <p className="section-label fade-up">Pricing</p>
        <h2 className="section-title fade-up delay-1">Simple, honest pricing.</h2>
        <p className="section-sub fade-up delay-2">No surprises. No hidden charges. Ever.</p>

        <div className="pricing-grid fade-up delay-2">
          {/* FREE card */}
          <div className="price-card featured">
            <div className="price-badge">&#127881; Limited time offer</div>
            <div className="price-plan-name">First Year</div>
            <div className="price-amount">FREE</div>
            <p className="price-note">
              Completely free for your entire first year.
              <br />
              No card. No catch. No conditions.
            </p>
            <ul className="price-features">
              <li>
                <span className="tick">&#10003;</span> No credit card required
              </li>
              <li>
                <span className="tick">&#10003;</span> No setup fee
              </li>
              <li>
                <span className="tick">&#10003;</span> No monthly charges
              </li>
              <li>
                <span className="tick">&#10003;</span> No hidden fees
              </li>
              <li>
                <span className="tick">&#10003;</span> Full product access, all features
              </li>
              <li>
                <span className="tick">&#10003;</span> Unlimited customer conversations
              </li>
            </ul>
          </div>

          {/* PAID card */}
          <div className="price-card">
            <div className="price-badge" style={{ background: "var(--green-pale)", color: "var(--green)" }}>
              After year 1
            </div>
            <div className="price-plan-name">Annual Plan</div>
            <div className="price-amount">
              <span className="currency">&#8377;</span>12,000<span className="period">/yr</span>
            </div>
            <p className="price-note">Billed once a year. That's just &#8377;1,000/month.</p>
            <ul className="price-features">
              <li>
                <span className="tick">&#10003;</span> Everything from year 1
              </li>
              <li>
                <span className="tick">&#10003;</span> Flat annual price — no tiers
              </li>
              <li>
                <span className="tick">&#10003;</span> Cancel any time
              </li>
              <li>
                <span className="tick">&#10003;</span> Priority support
              </li>
            </ul>
          </div>
        </div>

        <div className="price-divider fade-up delay-3">Zero fees in your first year</div>
        <div className="zero-fees-row fade-up delay-3">
          <div className="zero-fee-item">
            <span className="zf-icon">&#128179;</span> No credit card
          </div>
          <div className="zero-fee-item">
            <span className="zf-icon">&#9881;&#65039;</span> No setup fee
          </div>
          <div className="zero-fee-item">
            <span className="zf-icon">&#128274;</span> No lock-in
          </div>
          <div className="zero-fee-item">
            <span className="zf-icon">&#127381;</span> No auto-charge
          </div>
          <div className="zero-fee-item">
            <span className="zf-icon">&#10024;</span> No hidden cost
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section id="final-cta">
        <h2 className="fade-up">Ready to make WhatsApp work for your business?</h2>
        <p className="fade-up delay-1">
          Join hundreds of small businesses already saving time every week.
        </p>
        <Link to="/dashboard" className="btn-cta-white fade-up delay-2" id="final-get-started">
          <WhatsAppIcon />
          Get Started — Free
        </Link>
      </section>

      {/* FOOTER */}
      <footer>
        <span className="brand">DocLift</span>
        <span className="tagline">
          WhatsApp AI for small businesses &nbsp;&middot;&nbsp; Built in India &#127470;&#127475;
        </span>
      </footer>
    </div>
  );
};

export default HomeComponent;
